import { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

function playWhistle(red) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const t0 = ctx.currentTime;
    [0, 0.3].forEach((off) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(2100, t0 + off);
      osc.frequency.linearRampToValueAtTime(2600, t0 + off + 0.08);
      osc.frequency.linearRampToValueAtTime(2100, t0 + off + 0.18);
      gain.gain.setValueAtTime(0.0001, t0 + off);
      gain.gain.exponentialRampToValueAtTime(0.06, t0 + off + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + off + 0.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + off);
      osc.stop(t0 + off + 0.22);
    });
    if (red) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(130, t0 + 0.9);
      osc.frequency.exponentialRampToValueAtTime(45, t0 + 1.25);
      gain.gain.setValueAtTime(0.0001, t0 + 0.9);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.95);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + 0.9);
      osc.stop(t0 + 1.35);
    }
    setTimeout(() => ctx.close(), 2500);
  } catch {
    /* audio unavailable — silent */
  }
}

const CSS = `
.cr-overlay { position: fixed; inset: 0; z-index: 100; cursor: pointer; display: flex; align-items: center; justify-content: center;
  background: rgba(2, 6, 23, 0.62); backdrop-filter: blur(3px); animation: cr-dim var(--dur) ease forwards; }
@keyframes cr-dim { 0% { opacity: 0; } 7% { opacity: 1; } 87% { opacity: 1; } 100% { opacity: 0; } }

.cr-stage { position: relative; display: flex; align-items: flex-end; justify-content: center; }

.cr-ref-wrap { position: relative; animation: cr-slide var(--dur) cubic-bezier(.16,.84,.3,1) forwards; will-change: transform; }
.cr-red .cr-ref-wrap { animation-name: cr-slide-red; }
@keyframes cr-slide {
  0% { transform: translateX(-62vw) translateY(0); }
  6% { transform: translateX(-40vw) translateY(-5px); }
  12% { transform: translateX(-20vw) translateY(0); }
  18% { transform: translateX(-6vw) translateY(-3px); }
  24% { transform: translateX(0) translateY(0); }
  86% { transform: translateX(0) translateY(0) scale(1); }
  100% { transform: translateX(-3vw) translateY(14px) scale(.9); }
}
@keyframes cr-slide-red {
  0% { transform: translateX(-62vw) translateY(0); }
  6% { transform: translateX(-40vw) translateY(-5px); }
  12% { transform: translateX(-20vw) translateY(0); }
  18% { transform: translateX(-6vw) translateY(-3px); }
  24% { transform: translateX(0) translateY(0) scale(1); }
  28.5% { transform: translateX(2vw) scale(1.08); }
  32% { transform: translateX(0) scale(1); }
  34% { transform: translate(7px, -4px); } 36% { transform: translate(-7px, 4px); }
  38% { transform: translate(5px, 3px); } 40% { transform: translate(-4px, -2px); }
  42% { transform: translate(0, 0); }
  86% { transform: translateX(0) translateY(0) scale(1); }
  100% { transform: translateX(-3vw) translateY(14px) scale(.9); }
}
.cr-ref { height: min(52vh, 400px); filter: drop-shadow(0 18px 30px rgba(0,0,0,.45)); display: block; }

.cr-card { position: absolute; left: 2%; top: -7%; width: 64px; height: 92px; border-radius: 7px; opacity: 0;
  box-shadow: 0 10px 24px rgba(0,0,0,.5), inset 0 0 0 2px rgba(255,255,255,.25);
  animation: cr-pop var(--dur) cubic-bezier(.2,.7,.3,1.15) forwards; will-change: transform; }
@keyframes cr-pop {
  0%, 21% { transform: translate(46vw, -26vh) scale(.3) rotate(-28deg); opacity: 0; }
  23% { opacity: 1; }
  29% { transform: translate(0, 0) scale(1.28) rotate(5deg); opacity: 1; }
  34% { transform: scale(.95) rotate(-2deg); }
  38% { transform: scale(1.04) rotate(1deg); }
  42%, 86% { transform: scale(1) rotate(0); opacity: 1; }
  100% { transform: scale(.85); opacity: 0; }
}
.cr-card-fly { position: relative; left: auto; top: auto; animation-name: cr-fly; }
@keyframes cr-fly {
  0% { transform: translateY(-70vh) rotate(-32deg) scale(1.3); opacity: 0; }
  5% { opacity: 1; }
  27% { transform: translateY(0) rotate(4deg) scale(1.45); opacity: 1; }
  33% { transform: rotate(-2deg) scale(1.28); }
  38%, 86% { transform: rotate(0) scale(1.3); opacity: 1; }
  100% { transform: scale(1.05); opacity: 0; }
}

.cr-flash { position: fixed; inset: 0; pointer-events: none; opacity: 0; animation: cr-flash var(--dur) linear forwards; }
.cr-flash-y { background: #facc15; }
.cr-flash-r { background: #dc2626; }
.cr-flash-g { background: #16a34a; }
.cr-spark { position: absolute; border-radius: 9999px; pointer-events: none; opacity: 0;
  background: radial-gradient(circle, #bbf7d0 0%, #16a34a 60%, transparent 100%);
  animation: cr-ember var(--dur) ease-out forwards; }
@keyframes cr-flash {
  0%, 27.5% { opacity: 0; } 29.5% { opacity: var(--flash-op, .2); } 34%, 100% { opacity: 0; }
}

.cr-ember { position: absolute; border-radius: 9999px; pointer-events: none; opacity: 0;
  background: radial-gradient(circle, #fdba74 0%, #ea580c 60%, transparent 100%);
  animation: cr-ember var(--dur) ease-out forwards; }
@keyframes cr-ember {
  0%, 28% { transform: translate(0, 0) scale(1); opacity: 0; }
  31% { opacity: 1; }
  48%, 100% { transform: translate(var(--dx), -140px) scale(.2); opacity: 0; }
}

.cr-caption { position: absolute; bottom: 11vh; left: 0; right: 0; text-align: center; pointer-events: none;
  animation: cr-caption var(--dur) ease forwards; opacity: 0; }
@keyframes cr-caption {
  0%, 38% { opacity: 0; transform: translateY(14px); }
  46% { opacity: 1; transform: translateY(0); }
  84% { opacity: 1; } 100% { opacity: 0; }
}
.cr-title { font-size: clamp(20px, 3vw, 30px); font-weight: 800; color: #f8fafc; letter-spacing: .02em; text-shadow: 0 2px 14px rgba(0,0,0,.6); }
.cr-sub { margin-top: 6px; font-size: 14px; color: #cbd5e1; max-width: 480px; margin-left: auto; margin-right: auto; }
.cr-skip { position: absolute; bottom: 24px; right: 28px; font-size: 11px; color: rgba(226,232,240,.55); letter-spacing: .08em; text-transform: uppercase; pointer-events: none; }
.cr-mute { position: absolute; top: 20px; right: 24px; pointer-events: auto; display: flex; align-items: center; gap: 6px;
  border-radius: 9999px; border: 1px solid rgba(148,163,184,.35); background: rgba(15,23,42,.5);
  padding: 6px 12px; font-size: 11px; color: #cbd5e1; transition: background-color .15s; }
.cr-mute:hover { background: rgba(15,23,42,.85); }
`;

export function CardReveal({ color = "yellow", title, sub, onDone }) {
  const red = color === "red";
  const green = color === "green";
  const duration = red ? 3300 : 2700;
  const [imgOk, setImgOk] = useState(true);
  const [muted, setMuted] = useState(true);
  const doneRef = useRef(false);

  const embers = useMemo(
    () =>
      Array.from({ length: 16 }, () => ({
        left: 26 + Math.random() * 44,
        top: 10 + Math.random() * 26,
        size: 4 + Math.random() * 7,
        dx: `${Math.round(-50 + Math.random() * 100)}px`,
      })),
    []
  );

  useEffect(() => {
    const t = setTimeout(() => finish(), duration);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone?.();
  };

  const dur = `${duration}ms`;
  const cardBg = red ? "#dc2626" : green ? "#16a34a" : "#facc15";

  return (
    <div className="cr-overlay" style={{ "--dur": dur }} onClick={finish} data-testid="card-reveal-overlay">
      <style>{CSS}</style>
      <div className={`cr-stage ${red ? "cr-red" : ""}`} style={{ "--dur": dur }}>
        {imgOk ? (
          <div className="cr-ref-wrap" style={{ "--dur": dur }}>
            <img src="/referee.png" className="cr-ref" alt="Referee" onError={() => setImgOk(false)} data-testid="card-reveal-referee" />
            <div className="cr-card" style={{ "--dur": dur, backgroundColor: cardBg }} data-testid="card-reveal-card" />
            {red &&
              embers.map((e, i) => (
                <span key={i} className="cr-ember" style={{ "--dur": dur, "--dx": e.dx, left: `${e.left}%`, top: `${e.top}%`, width: e.size, height: e.size }} />
              ))}
            {green &&
              embers.map((e, i) => (
                <span key={i} className="cr-spark" style={{ "--dur": dur, "--dx": e.dx, left: `${e.left}%`, top: `${e.top}%`, width: e.size, height: e.size }} />
              ))}
          </div>
        ) : (
          <div className="cr-card cr-card-fly" style={{ "--dur": dur, backgroundColor: cardBg }} data-testid="card-reveal-card" />
        )}
        <div
          className={`cr-flash ${red ? "cr-flash-r" : green ? "cr-flash-g" : "cr-flash-y"}`}
          style={{ "--dur": dur, "--flash-op": red ? 0.34 : 0.2 }}
          data-testid="card-reveal-flash"
        />
      </div>
      <div className="cr-caption" style={{ "--dur": dur }}>
        <div className="cr-title" data-testid="card-reveal-title">{title}</div>
        {sub && <div className="cr-sub" data-testid="card-reveal-sub">{sub}</div>}
      </div>
      <div className="cr-skip">Tap anywhere to skip</div>
      <button
        type="button"
        className="cr-mute"
        data-testid="card-reveal-sound-toggle"
        onClick={(e) => {
          e.stopPropagation();
          if (muted) playWhistle(red);
          setMuted(!muted);
        }}
      >
        {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        {muted ? "Sound off" : "Sound on"}
      </button>
    </div>
  );
}
